import { createContext, useCallback, useContext, useState } from "react";

const CartContext = createContext({
    plan: null,
    extras: { kundeflow: 0, medarbejderflow: 0, kundeminutter: 0, kommandoer: 0 },
    agreement: "monthly",
    setPlan: () => {},
    incExtra: () => {},
    decExtra: () => {},
    setAgreement: () => {},
    reset: () => {},
});

const INITIAL_EXTRAS = { kundeflow: 0, medarbejderflow: 0, kundeminutter: 0, kommandoer: 0 };

export function CartProvider({ children }) {
    const [plan, setPlan] = useState(null);
    const [extras, setExtras] = useState(INITIAL_EXTRAS);
    const [agreement, setAgreement] = useState("monthly");

    const incExtra = useCallback((key) => {
        setExtras((e) => ({ ...e, [key]: Math.min((e[key] ?? 0) + 1, 99) }));
    }, []);
    const decExtra = useCallback((key) => {
        setExtras((e) => ({ ...e, [key]: Math.max((e[key] ?? 0) - 1, 0) }));
    }, []);
    const reset = useCallback(() => {
        setPlan(null);
        setExtras(INITIAL_EXTRAS);
        setAgreement("monthly");
    }, []);

    return (
        <CartContext.Provider value={{ plan, extras, agreement, setPlan, incExtra, decExtra, setAgreement, reset }}>
            {children}
        </CartContext.Provider>
    );
}

export function useCart() {
    return useContext(CartContext);
}

// Pricing constants
export const PLAN_PRICE = { starter: 6500, pro: 11500 };
export const EXTRA_PRICE = {
    kundeflow: 2200,           // /md. per flow
    medarbejderflow: 1900,     // /md. per flow
    kundeminutter: 600,        // /md. per 100-min block
    kommandoer: 400,           // /md. per 1.000-cmd block
};
export const AGREEMENT_DISCOUNT = {
    monthly: 0,
    "3months": 0.05,
    "12months": 0.15,
};

export function computeTotals({ plan, extras, agreement }) {
    const planPrice = plan ? PLAN_PRICE[plan] : 0;
    const monthlyExtras =
        (extras.kundeflow || 0) * EXTRA_PRICE.kundeflow +
        (extras.medarbejderflow || 0) * EXTRA_PRICE.medarbejderflow;
    const usageExtras =
        (extras.kundeminutter || 0) * EXTRA_PRICE.kundeminutter +
        (extras.kommandoer || 0) * EXTRA_PRICE.kommandoer;
    const monthly = planPrice + monthlyExtras;
    const subtotal = monthly + usageExtras;
    const discountRate = AGREEMENT_DISCOUNT[agreement] || 0;
    const discount = Math.round(monthly * discountRate);
    const total = subtotal - discount;
    return { planPrice, monthlyExtras, usageExtras, monthly, subtotal, discount, total, discountRate };
}

export function buildPrefillMessage({ plan, extras, agreement }) {
    const planLabel = plan === "starter" ? "Starter-pakken" : plan === "pro" ? "Pro-pakken" : null;
    if (!planLabel && !extras.kundeflow && !extras.medarbejderflow && !extras.kundeminutter && !extras.kommandoer) {
        return "";
    }
    const agreementLabel = agreement === "3months"
        ? "3 måneder"
        : agreement === "12months"
        ? "12 måneder"
        : "Månedlig";
    const totals = computeTotals({ plan, extras, agreement });
    const fmt = (n) => n.toLocaleString("da-DK") + " kr.";
    const lines = [];
    lines.push(`Jeg er interesseret i ${planLabel || "et setup uden valgt pakke"}.`);
    lines.push(`Ekstra kundeflows: ${extras.kundeflow || 0}`);
    lines.push(`Ekstra medarbejderflows: ${extras.medarbejderflow || 0}`);
    lines.push(`Ekstra kunde-minutter: ${extras.kundeminutter || 0}`);
    lines.push(`Ekstra medarbejderkommandoer: ${extras.kommandoer || 0}`);
    lines.push(`Aftaleperiode: ${agreementLabel}`);
    lines.push(`Estimeret total: ${fmt(totals.total)}`);
    return lines.join("\n");
}
